/**
 * Inventory Controller dashboard — Stock Reports.
 *
 * Dealer-lens questions this view answers:
 *   - How many units are on the floor right now?        → Total in stock
 *   - What's getting old?                               → Age-bucket distribution
 *   - Are we overexposed?                               → Aged-exposure count vs today's total
 *   - How fast are we moving stock?                     → Days-supply, with the 60-day benchmark
 *   - Where is the volume?                              → Top makes
 *
 * Deliberately nothing about leads, customers or deals — the Inventory
 * Controller's remit per the client's "Who gets what" grid is stock-only.
 * Reports from this screen never need to join a lead to a vehicle.
 */

import KpiTile from '../../components/dashboard/KpiTile';
import { DonutChart, ColumnChart } from '../../components/dashboard/Pictorial';

const AGE_LABELS = {
  '0_30':   '0–30 days',
  '31_60':  '31–60 days',
  '61_90':  '61–90 days',
  'OVER_90': '90+ days',
};
const AGE_COLORS = {
  '0_30':    'var(--success)',
  '31_60':   'var(--accent-primary)',
  '61_90':   'var(--warning)',
  'OVER_90': 'var(--danger)',
};

const fmtMoney = (cents) => cents == null ? '—'
  : `$${Math.round(cents / 100).toLocaleString('en-AU')}`;

export default function InventoryControllerDashboard({ inventory }) {
  const inv = inventory;
  const over60 = inv?.daysSupply != null && inv.daysSupplyBenchmarkHigh
    && inv.daysSupply > inv.daysSupplyBenchmarkHigh;
  const gaps = inv?.listingGaps || {};
  const totalGaps = Object.values(gaps).reduce((a, b) => a + Number(b || 0), 0);

  return (
    <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── ROW 1 — the four headline numbers ── */}
      <div className="grid-4">
        <KpiTile index={0} label="UNITS IN STOCK" periodLabel="Now" to="/inventory"
          value={inv?.totalInStock ?? '—'} />
        <KpiTile index={1} label="AGED 90+ DAYS" periodLabel="Now"
          value={inv?.agedExposureCount ?? '—'}
          color={(inv?.agedExposureCount ?? 0) > 0 ? 'var(--danger)' : 'var(--success)'}
          tone={(inv?.agedExposureCount ?? 0) > 0 ? 'danger' : undefined}
          sub="over the 90-day watermark" />
        <KpiTile index={2} label="DAYS SUPPLY" periodLabel="Rolling 30d"
          value={inv?.daysSupply == null ? '—' : Math.round(inv.daysSupply)}
          color={over60 ? 'var(--danger)' : 'var(--success)'}
          tone={over60 ? 'danger' : undefined}
          sub={`benchmark ≤ ${inv?.daysSupplyBenchmarkHigh ?? 60} days`} />
        <KpiTile index={3} label="FRESH ARRIVALS" periodLabel="0–30 days"
          value={inv?.byAge?.['0_30'] ?? '—'}
          sub="newly on the lot" />
      </div>

      {/* ── ROW 2 — pictorial breakdowns ── */}
      {inv && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
          <div className="card">
            <div className="card-label" style={{ marginBottom: 8 }}>STOCK AGE DISTRIBUTION</div>
            <DonutChart centerLabel="units" segments={Object.entries(inv.byAge || {}).map(([k, v]) => ({
              label: AGE_LABELS[k] || k,
              value: Number(v) || 0,
              color: AGE_COLORS[k] || 'var(--accent-primary)',
            }))} />
          </div>
          <div className="card">
            <div className="card-label" style={{ marginBottom: 8 }}>SPLIT BY CONDITION</div>
            <ColumnChart height={160} bars={Object.entries(inv.byCondition || {}).map(([k, v]) => ({
              label: k === 'UNKNOWN' ? 'Unset' : k.charAt(0) + k.slice(1).toLowerCase(),
              value: Number(v) || 0,
              color: k === 'NEW'  ? 'var(--accent-primary)'
                  : k === 'USED' ? 'var(--info)'
                  : k === 'DEMO' ? 'var(--warning)'
                                 : 'var(--text-tertiary)',
            }))} />
          </div>
          <div className="card">
            <div className="card-label" style={{ marginBottom: 8 }}>TOP MAKES IN STOCK</div>
            {(inv.topByMake || []).length === 0 ? (
              <div className="text-xs text-muted">No stock to summarise.</div>
            ) : (
              <ColumnChart height={160} bars={inv.topByMake.map((m, i) => ({
                label: m.make,
                value: Number(m.count) || 0,
                color: i === 0 ? 'var(--accent-primary)' : 'var(--info)',
              }))} />
            )}
          </div>
        </div>
      )}

      {/* ── ROW 3 — value, reservations, listing quality tiles ── */}
      {inv && (
        <div className="grid-4">
          <KpiTile index={0} label="STOCK VALUE" periodLabel="Now"
            value={fmtMoney(inv.stockValueCents)}
            sub="drive-away across the yard" />
          <KpiTile index={1} label="RESERVED" periodLabel="Now" to="/inventory"
            value={inv.reservedCount ?? 0}
            color={(inv.reservedCount ?? 0) > 0 ? 'var(--warning)' : undefined}
            sub="soft-held against live quotes" />
          <KpiTile index={2} label="LISTING GAPS" periodLabel="Now" to="/inventory"
            value={totalGaps}
            color={totalGaps > 0 ? 'var(--warning)' : 'var(--success)'}
            tone={totalGaps > 0 ? 'warning' : undefined}
            sub="fields missing across stock records" />
          <KpiTile index={3} label="UNPRICED CARS" periodLabel="Now" to="/inventory"
            value={gaps.missingPrice ?? 0}
            color={(gaps.missingPrice ?? 0) > 0 ? 'var(--danger)' : 'var(--success)'}
            tone={(gaps.missingPrice ?? 0) > 0 ? 'danger' : undefined}
            sub="cannot be listed without a price" />
        </div>
      )}

      {/* ── ROW 4 — the weekly aged-stock review + listing-quality detail ── */}
      {inv && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
          <div className="card">
            <div className="card-label" style={{ marginBottom: 8 }}>AGED-STOCK REVIEW · OLDEST FIRST</div>
            {(inv.oldestUnits || []).length === 0 ? (
              <div className="text-xs text-muted">Nothing in stock to review.</div>
            ) : (
              <table className="data-table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Vehicle</th><th>Stock #</th><th>Condition</th>
                    <th style={{ textAlign: 'right' }}>Price</th>
                    <th style={{ textAlign: 'right' }}>Days on lot</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.oldestUnits.map((u, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>
                        {[u.year, u.make, u.model].filter(Boolean).join(' ')}
                      </td>
                      <td className="mono text-xs">{u.stockId || '—'}</td>
                      <td>{u.condition ? u.condition.charAt(0) + u.condition.slice(1).toLowerCase() : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(u.priceCents)}</td>
                      <td style={{
                        textAlign: 'right', fontWeight: 700,
                        color: u.daysOnLot > 90 ? 'var(--danger)'
                             : u.daysOnLot > 60 ? 'var(--warning)'
                             : 'var(--text-primary)',
                      }}>{u.daysOnLot}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="card">
            <div className="card-label" style={{ marginBottom: 8 }}>LISTING QUALITY</div>
            {totalGaps === 0 ? (
              <div className="text-xs" style={{ color: 'var(--success)', lineHeight: 1.6 }}>
                Every in-stock car has photos, a price, a condition and a
                km reading — listings can go out accurately.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
                {[
                  ['Missing photos',    gaps.missingPhotos],
                  ['Missing price',     gaps.missingPrice],
                  ['Missing condition', gaps.missingCondition],
                  ['Missing km',        gaps.missingKm],
                ].map(([label, n]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="text-muted">{label}</span>
                    <span style={{
                      fontWeight: 700,
                      color: Number(n) > 0 ? 'var(--warning)' : 'var(--success)',
                    }}>{Number(n) || 0}</span>
                  </div>
                ))}
                <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
                  Fix these in Inventory so the online listings stay accurate.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!inv && (
        <div className="card">
          <div className="mono text-xs text-muted">Loading stock reports…</div>
        </div>
      )}
    </div>
  );
}
